import { date_dmy_func } from "../../components/Common/CommonFunction";
import { numberWithCommas } from "../Report_common_function";

/**
 * TableData.js — RA Masale format
 * ------------------------------------------------------------------
 * Pure data functions only (no doc.* drawing calls) — same split you
 * use in your existing TableData.js: this file returns arrays/rows/
 * strings, ReportStyle.js turns them into doc.text / autoTable calls.
 * ------------------------------------------------------------------
 */

export const columns = [
    "S.N",                    // 0
    "Description of Goods",   // 1
    "HSN",                    // 2
    "MRP",                    // 3
    "BATCH NO",                // 4
    "Qty",                    // 5
    "Unit",                   // 6
    "P.LIST",                 // 7
    "Disc",                   // 8
    "Gst %",                  // 9
    "Price",                  // 10
    "Amount",                 // 11
];

// Column widths in points. MUST sum to <= (page width - left/right
// margins) = 595 - 30 - 30 = 535. Kept at 534 for a little headroom.
// This is the fix for the earlier bug where widths summed to 591pt
// and silently clipped the Amount column off the page.
export const columnWidths = {
    0: 23,   // S.N
    1: 154,  // Description of Goods
    2: 43,   // HSN
    3: 37,   // MRP
    4: 37,   // BATCH NO
    5: 27,   // Qty
    6: 32,   // Unit
    7: 43,   // P.LIST
    8: 30,   // Disc
    9: 27,   // Gst %
    10: 43,  // Price
    11: 44,  // Amount
};

/**
 * Strip ALL stacked "-SubGroupName GroupName" repeats that get
 * appended when ModifyTableData_func runs more than once on the
 * same row object — same cleanup as the ItemName column formatter
 * in the items table, applied here so the printed invoice matches.
 */
const cleanItemName = (item) => {
    let name = item.ItemName || "";

    if (item.SubGroupName && item.GroupName) {
        const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const suffix = `-${item.SubGroupName} ${item.GroupName}`;
        const stackedSuffixRegex = new RegExp(`(?:${escapeRegExp(suffix)})+$`);
        name = name.replace(stackedSuffixRegex, "");
    }

    return name.replace(/\/-.*/, "/-");
};

const formatAmount = (value) => {
    const num = Number(value);

    return Number.isFinite(num)
        ? numberWithCommas(num.toFixed(2))
        : "";
};

export const Rows = (data) => {
    const items = data.InvoiceItems || [];

    let totalQty = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalIGST = 0;

    const returnArr = items.map((item, idx) => {
        totalQty += Number(item.Quantity) || 0;
        totalCGST += Number(item.CGST) || 0;
        totalSGST += Number(item.SGST) || 0;
        totalIGST += Number(item.IGST) || 0;

        return [
            idx + 1,
            cleanItemName(item),
            item.HSNCode || "",
            formatAmount(item.MRPValue),
            item.BatchCode || "",
            `${parseFloat(item.Quantity) || 0}`,
            item.PrimaryUnitName || "",
            formatAmount(item.BaseRate), // P.LIST
            `${parseFloat(((Number(item.CustomerMargin) || 0) + (Number(item.Discount) || 0)).toFixed(2))}${item.DiscountType === "1" ? " Rs" : "%"}`,
            `${Number(item.GSTPercentage) || 0}%`,
            formatAmount(item.Rate),
            formatAmount(item.BasicAmount),
        ];
    });

    data["TotalItemlength"] = items.length;

    data["footerTotals"] = {
        totalQty,
        totalCGST,
        totalSGST,
        totalIGST,
        isIGST: totalIGST > 0 && totalCGST === 0 && totalSGST === 0,
    };

    return returnArr;
};
/**
 * Best-effort city extraction for the "Station" field. Your API
 * response has no dedicated City field, only a free-text Address
 * plus a separate State. This strips the trailing state (and any
 * pincode) off the address and returns the last remaining
 * comma-segment as the city guess.
 *
 * Verified against both sample invoices:
 *   "...NEAR IDIA TOWER, TASGAON Maharashtra, 416312" -> "TASGAON"
 *   "...CIDCO, Nanded, Maharashtra"                   -> "Nanded"
 *
 * Replace with a real City field if/when your API adds one.
 */
export const deriveCity = (address, state) => {
    if (!address) return "";
    let cleaned = address;
    if (state) {
        cleaned = cleaned.replace(new RegExp(state + "\\s*,?\\s*\\d*\\s*$", "i"), "");
    }
    cleaned = cleaned.replace(/,\s*\d{6}\s*$/, ""); // trailing pincode
    const parts = cleaned.split(",").map((p) => p.trim()).filter(Boolean);
    return parts[parts.length - 1] || "";
};

/**
 * GST state code is the first 2 digits of any GSTIN. Used to render
 * "Place of Supply : Maharashtra (27)" the way the real invoice does.
 */
export const stateCodeFromGSTIN = (gstin) => {
    if (!gstin || gstin.length < 2) return "";
    return gstin.substring(0, 2);
};

/**
 * "Party Details :" box — this is your CUSTOMER (buyer), per the
 * reference image's labelling, not the seller. Returned as plain
 * strings/lines (not autoTable rows) so ReportStyle can control
 * exact wrapping and inline layout (e.g. GSTIN/FSSAI/Expiry flowing
 * across a wrapped line, same as the real invoice).
 */
export const PartyDetails = (data) => {
    let CustomerAddress = "";
    if (Array.isArray(data.CustomerAddress)) {
        const filteredArray = data.CustomerAddress.filter((obj) => obj.IsDefault === true);
        CustomerAddress = filteredArray[0] === undefined ? "" : filteredArray[0].Address;
    } else {
        CustomerAddress = data.CustomerAddress;
    }

    const gstinLine = [
        `GSTIN/UIN: ${data.CustomerGSTIN || ""}`,
        `FSSAI No: ${data.CustomerFSSAINo || ""}`,
        data.CustomerFSSAIExpiry ? `FSSAI Expiry  :   ${date_dmy_func ? date_dmy_func(data.CustomerFSSAIExpiry) : data.CustomerFSSAIExpiry}` : "",
    ].filter(Boolean).join("     ");

    return {
        name: data.CustomerName || "",
        address: CustomerAddress || "",
        gstinLine,
    };
};

/**
 * Right-hand invoice info block. Each entry is {label, value}. The
 * "Place of Supply" entry additionally carries {extraLabel, extraValue}
 * for the Reverse Charge flag, which sits on the SAME line in the
 * real invoice rather than its own row.
 */
export const InvoiceInfoRows = (data) => {
    const stateCode = stateCodeFromGSTIN(data.CustomerGSTIN);
    const placeOfSupply = data.CustomerState
        ? `${data.CustomerState}${stateCode ? ` (${stateCode})` : ""}`
        : "";

    const customerAddress = Array.isArray(data.CustomerAddress)
        ? (data.CustomerAddress.find((a) => a.IsDefault)?.Address || "")
        : (data.CustomerAddress || "");
    const station = data.CustomerCity || deriveCity(customerAddress, data.CustomerState);

    return [
        { label: "Invoice No", value: data.FullInvoiceNumber || "" },
        { label: "Dated", value: date_dmy_func ? date_dmy_func(data.InvoiceDate) : data.InvoiceDate || "" },
        {
            label: "Place of Supply",
            value: placeOfSupply,
            extraLabel: "Reverse Charge",
            extraValue: data.ReverseCharge || "",
        },
        { label: "GR/RR No", value: data.GRRRNo || "" },
        { label: "Transport", value: data.TransportName || "" },
        { label: "Vehicle No", value: data.VehicleNo || "" },
        { label: "Station", value: station },
        { label: "E-Way Bill No", value: data.InvoiceUploads?.[0]?.EwayBillNo || "" },
    ];
};

/**
 * IRN / Ack No / Ack Date — only present for e-invoiced documents.
 */
export const IRNLine = (data) => {
    const upload = data.InvoiceUploads?.[0] || {};

    const ackDate = upload.EInvoiceCreatedOn
        ? date_dmy_func(upload.EInvoiceCreatedOn.split("T")[0])
        : "";

    return {
        irn: upload.Irn || "",
        ackNo: upload.AckNo || "",
        ackDate,
    };
};
